import React, { useState, useRef } from 'react';
import LegacyClaimantsTable, { LegacyClaimantsTableRef } from '../components/LegacyClaimantsTable';
import LegacyClaimantDetails from '../components/LegacyClaimantDetails';
import LegacyImportModal from '../components/LegacyImportModal';
import { LegacyClaimant } from '../api/legacy';
import './LegacyClaims.css';

const LegacyClaims: React.FC = () => {
  const [selectedLegacyClaimant, setSelectedLegacyClaimant] = useState<LegacyClaimant | null>(null);
  const [showLegacyImport, setShowLegacyImport] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<LegacyClaimantsTableRef>(null);

  // Handle claimant selection from table
  const handleClaimantClick = (claimant: LegacyClaimant) => {
    setSelectedLegacyClaimant(claimant);
  };

  // Handle closing the claimant details
  const handleCloseDetails = () => {
    setSelectedLegacyClaimant(null);
  };

  // Handle updating claimant after audit
  const handleClaimantUpdate = async (updatedClaimant: LegacyClaimant) => {
    // Update the selected claimant if it's still open
    if (selectedLegacyClaimant?.id === updatedClaimant.id) {
      setSelectedLegacyClaimant(updatedClaimant);
    }
    
    // Trigger a refresh of the claimants table to show updated status
    if (tableRef.current) {
      await tableRef.current.refresh();
    }
  };

  // Handle file selection
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Convert FileList to array and filter for XML files
      const fileArray = Array.from(files);
      const xmlFiles = fileArray.filter(file => file.name.toLowerCase().endsWith('.xml'));
      
      // Check if any non-XML files were selected
      if (xmlFiles.length !== fileArray.length) {
        const nonXmlCount = fileArray.length - xmlFiles.length;
        alert(`${nonXmlCount} non-XML file${nonXmlCount > 1 ? 's were' : ' was'} excluded. Only XML files are accepted.`);
      }
      
      // Only proceed if we have at least one XML file
      if (xmlFiles.length > 0) {
        setSelectedFiles(xmlFiles);
        setShowLegacyImport(true);
      } else {
        alert('Please select at least one XML file');
      }
    }
    // Reset the input so the same files can be selected again
    if (event.target) {
      event.target.value = '';
    }
  };

  // Handle closing the import modal
  const handleCloseImport = () => {
    setShowLegacyImport(false);
    setSelectedFiles([]);
  };

  // Handle import completion
  const handleImportComplete = async () => {
    // Close the modal first
    handleCloseImport();
    
    // Trigger a refresh of the claimants table
    if (tableRef.current) {
      await tableRef.current.refresh();
    }
  };

  return (
    <div className="legacy-claims-page">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xml"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Page Header */}
      <div className="legacy-claims-header">
        <h1>Legacy Claims</h1>
        <button 
          className="import-legacy-button"
          onClick={handleImportClick}
        >
          Import Legacy XML
        </button>
      </div>

      {/* Main Content - Legacy Claimants Table */}
      <div className="legacy-claims-content">
        <LegacyClaimantsTable 
          ref={tableRef}
          onClaimantClick={handleClaimantClick}
        />
      </div>

      {/* Legacy Claimant Details Modal */}
      {selectedLegacyClaimant && (
        <LegacyClaimantDetails
          claimant={selectedLegacyClaimant}
          onClose={handleCloseDetails}
          onUpdate={handleClaimantUpdate}
        />
      )}

      {/* Legacy Import Modal - Always rendered, controlled by isOpen prop */}
      <LegacyImportModal
        isOpen={showLegacyImport}
        onClose={handleCloseImport}
        files={selectedFiles}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default LegacyClaims;